<template>
  <div
    ref="mapElement"
    class="min-h-80 w-full overflow-hidden rounded-lg"
    :class="mapHeightClass"
  />
  <Teleport v-if="searchContainer" :to="searchContainer">
    <GooglePlacesInput
      class="w-64"
      :placeholder="t('search')"
      @select-address="handleSelectAddress"
    />
  </Teleport>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  shallowRef,
  useTemplateRef,
  watch,
} from 'vue';
import Leaflet from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import { MAPBOX_ACCESS_TOKEN, MAPBOX_STYLE_PATH } from '@/constants/mapbox.constant.ts';
import GooglePlacesInput, {
  type ExtendedGeocodedLocation,
} from '@/components/GooglePlacesInput.vue';
import { useI18n } from 'vue-i18n';
import {
  convertGeometryToPolygonRings,
  convertPolygonsToMultiPolygon,
  isAreaChargeMultiPolygon,
  type AreaChargeMultiPolygon,
} from '@/views/yield-management/area-charges/area-charge-geometry.ts';

const props = defineProps<{
  initialGeometry?: unknown;
  heightClass?: string;
}>();

const { t } = useI18n();
const searchContainer = shallowRef<HTMLElement | null>(null);

const mapHeightClass = computed(() => props.heightClass ?? 'h-[70vh]');

const emit = defineEmits<{
  change: [geometry: AreaChargeMultiPolygon | null];
}>();

const mapElement = useTemplateRef<HTMLElement>('mapElement');
const map = shallowRef<Leaflet.Map | null>(null);
const appliedInitial = ref(false);

const polygonStyle: Leaflet.PathOptions = {
  color: '#0f766e',
  weight: 2,
  fillOpacity: 0.25,
};

const syncPolygonDraw = () => {
  if (!map.value) return;
  let hasPolygon = false;
  map.value.eachLayer((layer) => {
    if (layer instanceof Leaflet.Polygon) hasPolygon = true;
  });
  map.value.pm.Toolbar.setButtonDisabled('drawPolygon', hasPolygon);
};

const readGeometry = () => {
  if (!map.value) return;
  const polygons: number[][][][] = [];
  map.value.eachLayer((layer) => {
    if (!(layer instanceof Leaflet.Polygon)) return;
    polygons.push(...convertGeometryToPolygonRings(layer.toGeoJSON().geometry));
  });
  syncPolygonDraw();
  emit('change', convertPolygonsToMultiPolygon(polygons));
};

const drawGeometry = (geometry: AreaChargeMultiPolygon) => {
  if (!map.value) return;
  const layers: Leaflet.Polygon[] = [];
  Leaflet.geoJSON(geometry, {
    style: polygonStyle,
    onEachFeature(_feature, layer) {
      if (layer instanceof Leaflet.Polygon) layers.push(layer);
    },
  });
  for (const layer of layers) layer.addTo(map.value);
  if (!layers.length) return;
  const bounds = Leaflet.featureGroup(layers).getBounds();
  if (bounds.isValid()) map.value.fitBounds(bounds, { padding: [24, 24] });
};

const applyInitialGeometry = () => {
  if (!map.value || appliedInitial.value || !isAreaChargeMultiPolygon(props.initialGeometry)) {
    return;
  }
  appliedInitial.value = true;
  drawGeometry(props.initialGeometry);
  readGeometry();
};

onMounted(() => {
  if (!mapElement.value) return;
  const createdMap = Leaflet.map(mapElement.value, { pmIgnore: false }).setView([54, 15], 4);
  Leaflet.tileLayer(
    `https://api.mapbox.com/styles/v1/${MAPBOX_STYLE_PATH}/tiles/512/{z}/{x}/{y}?access_token=${MAPBOX_ACCESS_TOKEN}`,
    {
      attribution:
        '&copy; <a href="https://www.mapbox.com/about/maps/">Mapbox</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      tileSize: 512,
      zoomOffset: -1,
      maxZoom: 22,
      pmIgnore: true,
    },
  ).addTo(createdMap);

  const SearchControl = Leaflet.Control.extend({
    onAdd() {
      const container = Leaflet.DomUtil.create('div');
      Leaflet.DomEvent.disableClickPropagation(container);
      Leaflet.DomEvent.disableScrollPropagation(container);
      searchContainer.value = container;
      return container;
    },
  });
  new SearchControl({ position: 'topright' }).addTo(createdMap);

  createdMap.pm.setGlobalOptions({ continueDrawing: false });
  createdMap.pm.addControls({
    position: 'topleft',
    drawMarker: false,
    drawCircleMarker: false,
    drawPolyline: false,
    drawRectangle: false,
    drawPolygon: true,
    drawCircle: false,
    drawText: false,
    drawFreehand: false,
    editMode: true,
    dragMode: false,
    cutPolygon: true,
    removalMode: true,
    rotateMode: false,
    optionsControls: false,
    customControls: false,
    splitMode: false,
    scaleMode: false,
    unionMode: false,
    differenceMode: false,
    lineSimplificationMode: false,
    lassoMode: false,
    copyLayerMode: false,
    pinningOption: false,
    snappingOption: false,
    autoTracingOption: false,
    snapGuidesOption: false,
  });

  createdMap.on('pm:create', (event) => {
    if (event.layer instanceof Leaflet.Polygon) event.layer.setStyle(polygonStyle);
    createdMap.pm.disableDraw();
    readGeometry();
  });
  createdMap.on('pm:update', readGeometry);
  createdMap.on('pm:cut', readGeometry);
  createdMap.on('pm:remove', readGeometry);

  map.value = createdMap;
  applyInitialGeometry();
  void nextTick(() => createdMap.invalidateSize());
});

const handleSelectAddress = (location: ExtendedGeocodedLocation | null) => {
  const coordinates = location?.coordinates;
  if (!coordinates || !map.value) return;
  map.value.flyTo([coordinates.latitude, coordinates.longitude], 14);
};

watch(() => props.initialGeometry, applyInitialGeometry);

onBeforeUnmount(() => {
  map.value?.remove();
  map.value = null;
});
</script>

<i18n lang="json">
{
  "en": { "search": "Search place" },
  "de": { "search": "Ort suchen" },
  "es": { "search": "Buscar lugar" }
}
</i18n>
